import { ref, reactive, computed } from 'vue'
import { useApi } from './useApi'

const AVAILABLE_MODELS = [
  { key: 'gemini-3-pro', label: 'Gemini 3 Pro' },
  { key: 'gemini-3-flash', label: 'Gemini 3 Flash' },
  { key: 'claude-sonnet', label: 'Claude Sonnet' },
]

const DEFAULT_MODEL = 'gemini-3-pro'

export function useWorkbenchStore(csrfToken) {
  const api = useApi(csrfToken)

  // State
  const projects = ref([])
  const allAuthors = ref([])
  const currentProject = ref(null)
  const currentAuthor = ref(null)
  const currentContent = ref(null)
  const authorOverrides = reactive({})
  const projectOverrides = reactive({})
  const rightRolloutOpen = ref(false)
  const rightRolloutMode = ref('author-edit')
  const editingPane = ref(null) // null | 'author' | 'project'
  const leftDrawerOpen = ref(false)
  const rightDrawerOpen = ref(false)
  const loading = ref(false)
  const inputForm = ref(null)
  const projectContents = ref([])
  const activeTab = ref('voice')

  // Model selection (persisted in localStorage)
  const selectedModel = ref((() => {
    try { return localStorage.getItem('workbench:model') || DEFAULT_MODEL } catch { return DEFAULT_MODEL }
  })())

  function setModel(key) {
    selectedModel.value = key
    try { localStorage.setItem('workbench:model', key) } catch {}
  }

  // Publications (plan tab)
  const publications = ref([])
  const selectedPublication = ref(null)
  const selectedEntryId = ref(null)

  // Plan generation state
  const planBrief = ref('')
  const planCount = ref(10)
  const planGenerating = ref(false)
  const planStreamingText = ref('')
  const planParsedEntries = ref([])
  const planGenerationDone = ref(false)
  const planGenerationError = ref(null)
  const planSaving = ref(false)
  const planSaveError = ref(null)
  const planSavedPublication = ref(null)
  let planAbortController = null

  // Generation state (content)
  const generating = ref(false)
  const streamingText = ref('')
  const generationError = ref(null)
  let abortController = null

  // CMS state
  const cmsConnections = ref([])
  const cmsPublishing = ref(false)
  const cmsPublishError = ref(null)
  const cmsMetadataGenerating = ref(false)
  const dialogMode = ref(null) // null (closed), 'seo', 'publish'

  // Computed: effective (base merged with overrides)
  // Works even without base author — overrides become the values
  const effectiveAuthor = computed(() => {
    const base = currentAuthor.value || {}
    if (Object.keys(authorOverrides).length === 0) return currentAuthor.value
    return { ...base, ...authorOverrides }
  })

  const effectiveProject = computed(() => {
    if (!currentProject.value) return null
    return { ...currentProject.value, ...projectOverrides }
  })

  const authorOverriddenFields = computed(() => Object.keys(authorOverrides))
  const projectOverriddenFields = computed(() => Object.keys(projectOverrides))

  // Actions
  async function loadProjects() {
    loading.value = true
    try {
      projects.value = await api.get('/api/projects')
    } finally {
      loading.value = false
    }
  }

  async function loadAllAuthors() {
    try {
      allAuthors.value = await api.get('/api/authors')
    } catch (e) {
      console.error('Failed to load authors', e)
    }
  }

  async function createProject(name) {
    loading.value = true
    try {
      const project = await api.post('/api/projects', { project: { name } })
      projects.value.push(project)
      await selectProject(project.id)
      return project
    } finally {
      loading.value = false
    }
  }

  async function selectProject(id) {
    loading.value = true
    clearProjectOverrides()
    clearAuthorOverrides()
    currentContent.value = null
    try {
      currentProject.value = await api.get(`/api/projects/${id}`)
      try { localStorage.setItem('workbench:projectId', id) } catch {}
      // Auto-select first author if available
      if (currentProject.value.authors?.length > 0) {
        await selectAuthor(currentProject.value.authors[0].id)
      } else {
        currentAuthor.value = null
      }
      await loadProjectContents(id)
      await loadPublications(id)
    } finally {
      loading.value = false
    }
  }

  async function loadPublications(projectId) {
    try {
      publications.value = await api.get(`/api/projects/${projectId}/publications`)
    } catch (e) {
      console.error('Failed to load publications', e)
    }
  }

  async function loadProjectContents(projectId) {
    try {
      projectContents.value = await api.get(`/api/projects/${projectId}/contents`)
    } catch (e) {
      console.error('Failed to load project contents', e)
    }
  }

  async function selectAuthor(id) {
    loading.value = true
    clearAuthorOverrides()
    try {
      currentAuthor.value = await api.get(`/api/authors/${id}`)

      // Auto-assign to project if not already assigned
      if (currentProject.value) {
        const assigned = currentProject.value.authors?.some(a => a.id === id)
        if (!assigned) {
          await assignAuthorToProject(id)
        }
      }
    } finally {
      loading.value = false
    }
  }

  async function assignAuthorToProject(authorId) {
    if (!currentProject.value) return
    try {
      const result = await api.post(
        `/api/projects/${currentProject.value.id}/assign_author`,
        { author_id: authorId }
      )
      // Update project's authors list
      currentProject.value.authors = result.authors
    } catch (e) {
      console.error('Failed to assign author', e)
    }
  }

  async function unassignAuthorFromProject(authorId) {
    if (!currentProject.value) return
    try {
      const result = await api.delete(
        `/api/projects/${currentProject.value.id}/unassign_author?author_id=${authorId}`
      )
      currentProject.value.authors = result.authors
      if (currentAuthor.value?.id === authorId) {
        currentAuthor.value = null
        clearAuthorOverrides()
      }
    } catch (e) {
      console.error('Failed to unassign author', e)
    }
  }

  function setAuthorOverride(field, value) {
    authorOverrides[field] = value
  }

  function setProjectOverride(field, value) {
    projectOverrides[field] = value
  }

  function clearAuthorOverrides() {
    for (const key of Object.keys(authorOverrides)) {
      delete authorOverrides[key]
    }
  }

  function clearProjectOverrides() {
    for (const key of Object.keys(projectOverrides)) {
      delete projectOverrides[key]
    }
  }

  function clearAuthorOverride(field) {
    delete authorOverrides[field]
  }

  function clearProjectOverride(field) {
    delete projectOverrides[field]
  }

  async function saveBackToAuthor() {
    if (!currentAuthor.value) return
    const updates = { ...authorOverrides }
    loading.value = true
    try {
      currentAuthor.value = await api.patch(`/api/authors/${currentAuthor.value.id}`, { author: updates })
      clearAuthorOverrides()
    } finally {
      loading.value = false
    }
  }

  async function saveBackToProject() {
    if (!currentProject.value) return
    const updates = { ...projectOverrides }
    loading.value = true
    try {
      const updated = await api.patch(`/api/projects/${currentProject.value.id}`, { project: updates })
      Object.assign(currentProject.value, updated)
      clearProjectOverrides()
    } finally {
      loading.value = false
    }
  }

  function newContent() {
    currentContent.value = null
    inputForm.value = { brief: '', title: '', format: 'article' }
  }

  async function deleteContent(contentId) {
    if (!currentProject.value?.id) return
    try {
      await api.delete(`/api/projects/${currentProject.value.id}/contents/${contentId}`)
      projectContents.value = projectContents.value.filter(c => c.id !== contentId)
      if (currentContent.value?.id === contentId) {
        currentContent.value = null
      }
    } catch (e) {
      console.error('Delete content error:', e)
    }
  }

  async function saveContent(contentData) {
    loading.value = true
    try {
      if (currentContent.value?.id) {
        currentContent.value = await api.patch(
          `/api/projects/${currentProject.value.id}/contents/${currentContent.value.id}`,
          { content: contentData }
        )
      } else {
        currentContent.value = await api.post(
          `/api/projects/${currentProject.value.id}/contents`,
          { content: contentData }
        )
      }
      // Refresh the content list
      if (currentProject.value?.id) {
        loadProjectContents(currentProject.value.id)
      }
      return currentContent.value
    } finally {
      loading.value = false
    }
  }

  async function createVersion(body, changeSummary = '') {
    if (!currentContent.value?.id) return
    loading.value = true
    try {
      const version = await api.post(
        `/api/contents/${currentContent.value.id}/content_versions`,
        { content_version: { body, change_summary: changeSummary } }
      )
      if (!currentContent.value.content_versions) {
        currentContent.value.content_versions = []
      }
      currentContent.value.content_versions.unshift(version)
      return version
    } finally {
      loading.value = false
    }
  }

  async function updateVersion(versionId, updates) {
    if (!currentContent.value?.id) return
    loading.value = true
    try {
      const version = await api.patch(
        `/api/contents/${currentContent.value.id}/content_versions/${versionId}`,
        { content_version: updates }
      )
      // Update the version in the local array
      const versions = currentContent.value.content_versions || []
      const idx = versions.findIndex(v => v.id === versionId)
      if (idx !== -1) {
        versions[idx] = { ...versions[idx], ...version }
      }
      return version
    } finally {
      loading.value = false
    }
  }

  async function rateContent(rating) {
    if (!currentContent.value?.id || !currentProject.value?.id) return
    try {
      currentContent.value = await api.patch(
        `/api/projects/${currentProject.value.id}/contents/${currentContent.value.id}`,
        { content: { rating } }
      )
      // Update in projectContents list too
      const idx = projectContents.value.findIndex(c => c.id === currentContent.value.id)
      if (idx !== -1) {
        projectContents.value[idx] = { ...projectContents.value[idx], rating }
      }
    } catch (e) {
      console.error('Rate content error:', e)
    }
  }

  async function rateVersion(versionId, rating) {
    if (!currentContent.value?.id) return
    try {
      await api.patch(
        `/api/contents/${currentContent.value.id}/content_versions/${versionId}`,
        { content_version: { rating } }
      )
      const versions = currentContent.value.content_versions || []
      const idx = versions.findIndex(v => v.id === versionId)
      if (idx !== -1) {
        versions[idx] = { ...versions[idx], rating }
      }
    } catch (e) {
      console.error('Rate version error:', e)
    }
  }

  async function setActiveVersion(versionId) {
    if (!currentContent.value?.id || !currentProject.value?.id) return
    currentContent.value.active_version_id = versionId
    try {
      await api.patch(
        `/api/projects/${currentProject.value.id}/contents/${currentContent.value.id}`,
        { content: { active_version_id: versionId } }
      )
    } catch (e) {
      console.error('Set active version error:', e)
    }
  }

  // --- Generation via SSE ---

  function getCsrfToken() {
    return csrfToken || document.querySelector('meta[name="csrf-token"]')?.content || ''
  }

  async function streamSSE(url, body, onChunk, onDone) {
    abortController = new AbortController()
    generating.value = true
    streamingText.value = ''
    generationError.value = null

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          'X-CSRF-Token': getCsrfToken(),
        },
        credentials: 'same-origin',
        body: JSON.stringify(body),
        signal: abortController.signal,
      })

      if (!response.ok) {
        const text = await response.text()
        throw new Error(`Generation failed: ${response.status} ${text}`)
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() // keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('event: done')) {
            // Next data line has the done payload
            continue
          }
          if (line.startsWith('event: error')) {
            continue
          }
          if (line.startsWith('data: ')) {
            const data = line.slice(6)
            try {
              const parsed = JSON.parse(data)
              if (typeof parsed === 'object' && parsed !== null && parsed.event) {
                if (parsed.event === 'done') {
                  onDone?.(parsed)
                } else if (parsed.event === 'error') {
                  generationError.value = parsed.message
                }
                continue
              }
              // JSON-decoded text chunk (string)
              if (typeof parsed === 'string') {
                streamingText.value += parsed
                onChunk?.(parsed)
              }
            } catch {
              // Fallback: raw text chunk
              streamingText.value += data
              onChunk?.(data)
            }
          }
        }
      }
    } catch (e) {
      if (e.name === 'AbortError') {
        // User cancelled
      } else {
        generationError.value = e.message
        console.error('Generation error:', e)
      }
    } finally {
      generating.value = false
      abortController = null
    }
  }

  async function generate({ rewrite = false } = {}) {
    if (!currentContent.value?.id) return

    const body = {
      model: selectedModel.value,
      author_overrides: Object.keys(authorOverrides).length ? { ...authorOverrides } : null,
      project_overrides: Object.keys(projectOverrides).length ? { ...projectOverrides } : null,
    }
    if (rewrite) body.rewrite = true

    await streamSSE(
      `/api/contents/${currentContent.value.id}/generate`,
      body,
      null, // onChunk — streamingText is updated in streamSSE
      (donePayload) => {
        // Generation complete — use clean body from server (outline stripped)
        if (donePayload.version_id) {
          const body = donePayload.body || streamingText.value
          if (!currentContent.value.content_versions) {
            currentContent.value.content_versions = []
          }
          currentContent.value.content_versions.unshift({
            id: donePayload.version_id,
            version_number: donePayload.version_number,
            body,
            language: currentProject.value?.language || null,
            change_summary: 'Generated',
          })
          currentContent.value.body = body
        }
      }
    )
  }

  async function regenerateBlocks({ fullBody, blockIndices, instruction }) {
    if (!currentContent.value?.id) return

    // Capture original body BEFORE streaming starts (streaming overwrites rawBody)
    const originalBody = fullBody

    const body = {
      model: selectedModel.value,
      full_body: fullBody,
      block_indices: blockIndices,
      instruction,
      author_overrides: Object.keys(authorOverrides).length ? { ...authorOverrides } : null,
      project_overrides: Object.keys(projectOverrides).length ? { ...projectOverrides } : null,
    }

    await streamSSE(
      `/api/contents/${currentContent.value.id}/regenerate_blocks`,
      body,
      null,
      () => {
        // On done, apply the replacement text to the selected blocks
        if (store._applyBlockReplacements) {
          store._applyBlockReplacements(blockIndices, streamingText.value, originalBody)
        }
      }
    )
  }

  async function translate(targetLanguage, sourceVersionId) {
    if (!currentContent.value?.id) return

    const body = {
      model: selectedModel.value,
      target_language: targetLanguage,
      source_version_id: sourceVersionId || null,
    }

    await streamSSE(
      `/api/contents/${currentContent.value.id}/translate`,
      body,
      null,
      (donePayload) => {
        if (donePayload.version_id) {
          const translatedBody = donePayload.body || streamingText.value
          if (!currentContent.value.content_versions) {
            currentContent.value.content_versions = []
          }
          currentContent.value.content_versions.unshift({
            id: donePayload.version_id,
            version_number: donePayload.version_number,
            body: translatedBody,
            language: targetLanguage,
            change_summary: `Translated to ${targetLanguage}`,
          })
        }
      }
    )
  }

  function stopGeneration() {
    abortController?.abort()
  }

  // --- Plan generation ---

  function resetPlanGeneration() {
    planStreamingText.value = ''
    planGenerationError.value = null
    planParsedEntries.value = []
    planGenerationDone.value = false
    planSavedPublication.value = null
    planSaveError.value = null
  }

  function parsePlanEntries(text) {
    const lines = text.split('\n')
    const entries = []
    const re = /^\d+\.\s+\*\*(.+?)\*\*\s*[—–\-]\s*(.+)/

    for (const line of lines) {
      const match = line.match(re)
      if (match) {
        entries.push({
          title: match[1].trim(),
          description: match[2].trim(),
        })
      }
    }
    return entries
  }

  async function generatePlan() {
    if (!currentProject.value?.id || !planBrief.value.trim()) return

    resetPlanGeneration()
    planAbortController = new AbortController()
    planGenerating.value = true

    try {
      const response = await fetch(
        `/api/projects/${currentProject.value.id}/generate_plan`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
            'X-CSRF-Token': getCsrfToken(),
          },
          credentials: 'same-origin',
          body: JSON.stringify({ brief: planBrief.value, count: planCount.value, model: selectedModel.value }),
          signal: planAbortController.signal,
        }
      )

      if (!response.ok) {
        const text = await response.text()
        throw new Error(`Generation failed: ${response.status} ${text}`)
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop()

        for (const line of lines) {
          if (line.startsWith('event: ')) continue
          if (line.startsWith('data: ')) {
            const data = line.slice(6)
            try {
              const parsed = JSON.parse(data)
              if (typeof parsed === 'object' && parsed !== null && parsed.event) {
                if (parsed.event === 'done') {
                  planGenerationDone.value = true
                  planParsedEntries.value = parsePlanEntries(planStreamingText.value)
                } else if (parsed.event === 'error') {
                  planGenerationError.value = parsed.message
                }
                continue
              }
              if (typeof parsed === 'string') {
                planStreamingText.value += parsed
              }
            } catch {
              planStreamingText.value += data
            }
          }
        }
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        planGenerationError.value = e.message
        console.error('Plan generation error:', e)
      }
    } finally {
      planGenerating.value = false
      planAbortController = null
    }
  }

  function stopPlanGeneration() {
    planAbortController?.abort()
  }

  function removePlanEntry(index) {
    planParsedEntries.value.splice(index, 1)
  }

  function updatePlanEntryTitle(index, title) {
    planParsedEntries.value[index].title = title
  }

  function updatePlanEntryDescription(index, description) {
    planParsedEntries.value[index].description = description
  }

  async function savePlan(publicationName) {
    if (!currentProject.value?.id || !planParsedEntries.value.length) return

    planSaving.value = true
    planSaveError.value = null
    try {
      const result = await api.post(
        `/api/projects/${currentProject.value.id}/publications`,
        {
          name: publicationName || `Plan: ${planBrief.value.slice(0, 50)}`,
          entries: planParsedEntries.value,
        }
      )
      planSavedPublication.value = result
      await loadPublications(currentProject.value.id)
      // Auto-select the new publication so user stays in editing mode
      selectedPublication.value = result
      selectedEntryId.value = null
      resetPlanGeneration()
      return result
    } catch (e) {
      planSaveError.value = e.message
      console.error('Save plan error:', e)
    } finally {
      planSaving.value = false
    }
  }

  async function createPublication() {
    if (!currentProject.value?.id) return
    try {
      const result = await api.post(
        `/api/projects/${currentProject.value.id}/publications`,
        { name: 'Untitled plan', entries: [] }
      )
      publications.value.unshift(result)
      selectedPublication.value = result
      selectedEntryId.value = null
      resetPlanGeneration()
      return result
    } catch (e) {
      console.error('Create publication error:', e)
    }
  }

  async function deletePublication(pubId) {
    if (!currentProject.value?.id) return
    try {
      await api.delete(`/api/projects/${currentProject.value.id}/publications/${pubId}`)
      publications.value = publications.value.filter(p => p.id !== pubId)
      if (selectedPublication.value?.id === pubId) {
        selectedPublication.value = null
        selectedEntryId.value = null
      }
    } catch (e) {
      console.error('Delete publication error:', e)
    }
  }

  function selectPublication(pub) {
    selectedPublication.value = pub
    selectedEntryId.value = null
    resetPlanGeneration()
  }

  function deselectPublication() {
    selectedPublication.value = null
    selectedEntryId.value = null
  }

  function selectEntry(pub, entryId) {
    selectedPublication.value = pub
    selectedEntryId.value = entryId
  }

  async function updatePublicationName(pubId, name) {
    if (!currentProject.value?.id) return
    try {
      const updated = await api.patch(
        `/api/projects/${currentProject.value.id}/publications/${pubId}`,
        { name }
      )
      if (selectedPublication.value?.id === pubId) {
        selectedPublication.value = updated
      }
      // Update in publications list
      const idx = publications.value.findIndex(p => p.id === pubId)
      if (idx !== -1) publications.value[idx] = updated
    } catch (e) {
      console.error('Update publication name error:', e)
    }
  }

  async function updatePublicationEntry(pubId, entryId, updates) {
    try {
      const updated = await api.patch(
        `/api/publications/${pubId}/publication_entries/${entryId}`,
        updates
      )
      // Update in selectedPublication
      if (selectedPublication.value?.id === pubId) {
        const idx = selectedPublication.value.entries.findIndex(e => e.id === entryId)
        if (idx !== -1) selectedPublication.value.entries[idx] = updated
      }
      // Update in publications list
      const pubIdx = publications.value.findIndex(p => p.id === pubId)
      if (pubIdx !== -1) {
        const entryIdx = publications.value[pubIdx].entries.findIndex(e => e.id === entryId)
        if (entryIdx !== -1) publications.value[pubIdx].entries[entryIdx] = updated
      }
    } catch (e) {
      console.error('Update entry error:', e)
    }
  }

  async function addPublicationEntry(pubId, title = 'Untitled') {
    try {
      const entry = await api.post(
        `/api/publications/${pubId}/publication_entries`,
        { title: title || 'Untitled', description: '' }
      )
      // Append to the publication in the list (selectedPublication may be the same object)
      const pubIdx = publications.value.findIndex(p => p.id === pubId)
      if (pubIdx !== -1) {
        publications.value[pubIdx].entries.push(entry)
      } else if (selectedPublication.value?.id === pubId) {
        selectedPublication.value.entries.push(entry)
      }
      return entry
    } catch (e) {
      console.error('Add entry error:', e)
    }
  }

  async function removePublicationEntry(pubId, entryId) {
    try {
      await api.delete(`/api/publications/${pubId}/publication_entries/${entryId}`)
      // Clear selection if this entry was selected
      if (selectedEntryId.value === entryId) {
        selectedEntryId.value = null
      }
      // Update the publication in the list (selectedPublication may be the same object)
      const pubIdx = publications.value.findIndex(p => p.id === pubId)
      if (pubIdx !== -1) {
        publications.value[pubIdx].entries = publications.value[pubIdx].entries.filter(e => e.id !== entryId)
      } else if (selectedPublication.value?.id === pubId) {
        selectedPublication.value.entries = selectedPublication.value.entries.filter(e => e.id !== entryId)
      }
    } catch (e) {
      console.error('Remove entry error:', e)
    }
  }

  async function appendEntriesToPublication(pubId, entries) {
    if (!currentProject.value?.id) return
    try {
      const updated = await api.post(
        `/api/projects/${currentProject.value.id}/publications/${pubId}/append_entries`,
        { entries }
      )
      // Replace selectedPublication with fresh data
      if (selectedPublication.value?.id === pubId) {
        selectedPublication.value = updated
      }
      // Update in publications list
      const idx = publications.value.findIndex(p => p.id === pubId)
      if (idx !== -1) publications.value[idx] = updated
      resetPlanGeneration()
      return updated
    } catch (e) {
      console.error('Append entries error:', e)
    }
  }

  // --- CMS ---

  async function loadCmsConnections() {
    try {
      cmsConnections.value = await api.get('/api/cms_connections')
    } catch (e) {
      console.error('Failed to load CMS connections', e)
    }
  }

  async function createCmsConnection(data) {
    try {
      const conn = await api.post('/api/cms_connections', { cms_connection: data })
      cmsConnections.value.push(conn)
      return conn
    } catch (e) {
      console.error('Create CMS connection error:', e)
      throw e
    }
  }

  async function deleteCmsConnection(id) {
    try {
      await api.delete(`/api/cms_connections/${id}`)
      cmsConnections.value = cmsConnections.value.filter(c => c.id !== id)
      // Clear from current project if it was using this connection
      if (currentProject.value?.cms_connection?.id === id) {
        currentProject.value.cms_connection = null
        currentProject.value.cms_connection_id = null
      }
    } catch (e) {
      console.error('Delete CMS connection error:', e)
    }
  }

  async function testCmsConnection(id) {
    try {
      const result = await api.post(`/api/cms_connections/${id}/test`, {})
      return result
    } catch (e) {
      return { status: 'error', message: e.message || 'Connection failed' }
    }
  }

  async function setCmsConnection(connectionId) {
    if (!currentProject.value) return
    try {
      const updated = await api.patch(`/api/projects/${currentProject.value.id}`, {
        project: { cms_connection_id: connectionId || null }
      })
      currentProject.value.cms_connection_id = connectionId || null
      // Reload project to get cms_connection data
      if (connectionId) {
        const conn = cmsConnections.value.find(c => c.id === connectionId)
        currentProject.value.cms_connection = conn || null
      } else {
        currentProject.value.cms_connection = null
      }
    } catch (e) {
      console.error('Set CMS connection error:', e)
    }
  }

  async function generateCmsMetadata() {
    if (!currentContent.value?.id) return
    cmsMetadataGenerating.value = true
    try {
      const result = await api.post(`/api/contents/${currentContent.value.id}/generate_metadata`, {})
      currentContent.value.cms_metadata = result.cms_metadata
      return result.cms_metadata
    } catch (e) {
      console.error('Generate CMS metadata error:', e)
      throw e
    } finally {
      cmsMetadataGenerating.value = false
    }
  }

  async function saveCmsMetadata(metadata) {
    if (!currentContent.value?.id || !currentProject.value?.id) return
    try {
      currentContent.value = await api.patch(
        `/api/projects/${currentProject.value.id}/contents/${currentContent.value.id}`,
        { content: { cms_metadata_json: JSON.stringify(metadata) } }
      )
      return currentContent.value.cms_metadata
    } catch (e) {
      console.error('Save CMS metadata error:', e)
      throw e
    }
  }

  async function pushToCms(metadata) {
    if (!currentContent.value?.id) return
    cmsPublishing.value = true
    cmsPublishError.value = null
    try {
      // Save metadata first if provided
      if (metadata) {
        currentContent.value = await api.patch(
          `/api/projects/${currentProject.value.id}/contents/${currentContent.value.id}`,
          { content: { cms_metadata_json: JSON.stringify(metadata) } }
        )
      }
      const result = await api.post(`/api/contents/${currentContent.value.id}/push_to_cms`, {})
      currentContent.value.cms_article_id = result.cms_article_id
      currentContent.value.cms_pushed_at = result.cms_pushed_at
      currentContent.value.status = result.status
      currentContent.value.published_at = result.published_at
      // Update sidebar list item so green dot appears immediately
      const idx = projectContents.value.findIndex(c => c.id === currentContent.value.id)
      if (idx !== -1) {
        projectContents.value[idx] = { ...projectContents.value[idx], cms_pushed_at: result.cms_pushed_at, status: result.status }
      }
      return result
    } catch (e) {
      cmsPublishError.value = e.message || 'Failed to push to CMS'
      throw e
    } finally {
      cmsPublishing.value = false
    }
  }

  function openEditPane(pane) {
    editingPane.value = pane
  }

  function closeEditPane() {
    editingPane.value = null
  }

  async function uploadSourceFile(contentId, file) {
    const formData = new FormData()
    formData.append('file', file)
    try {
      const response = await fetch(`/api/contents/${contentId}/content_sources`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': getCsrfToken(),
          'Accept': 'application/json',
        },
        credentials: 'same-origin',
        body: formData,
      })
      const data = await response.json()
      if (!response.ok) {
        console.error('Upload failed:', data)
        return null
      }
      return data
    } catch (e) {
      console.error('Upload error:', e)
      return null
    }
  }

  async function deleteSourceFile(contentId, sourceId) {
    try {
      await fetch(`/api/contents/${contentId}/content_sources/${sourceId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': getCsrfToken(),
          'Accept': 'application/json',
        },
        credentials: 'same-origin',
      })
    } catch (e) {
      console.error('Delete source file error:', e)
    }
  }

  async function loadContent(projectId, contentId) {
    loading.value = true
    try {
      currentContent.value = await api.get(`/api/projects/${projectId}/contents/${contentId}`)
    } finally {
      loading.value = false
    }
  }

  const store = {
    projects,
    allAuthors,
    currentProject,
    currentAuthor,
    currentContent,
    authorOverrides,
    projectOverrides,
    rightRolloutOpen,
    rightRolloutMode,
    editingPane,
    leftDrawerOpen,
    rightDrawerOpen,
    loading,
    inputForm,
    projectContents,
    activeTab,
    publications,
    selectedPublication,
    selectedEntryId,
    planBrief,
    planCount,
    planGenerating,
    planStreamingText,
    planParsedEntries,
    planGenerationDone,
    planGenerationError,
    planSaving,
    planSaveError,
    planSavedPublication,
    generating,
    streamingText,
    generationError,
    cmsConnections,
    cmsPublishing,
    cmsPublishError,
    cmsMetadataGenerating,
    dialogMode,
    selectedModel,
    availableModels: AVAILABLE_MODELS,
    setModel,
    effectiveAuthor,
    effectiveProject,
    authorOverriddenFields,
    projectOverriddenFields,
    loadProjects,
    loadAllAuthors,
    createProject,
    selectProject,
    selectAuthor,
    assignAuthorToProject,
    unassignAuthorFromProject,
    setAuthorOverride,
    setProjectOverride,
    clearAuthorOverrides,
    clearProjectOverrides,
    clearAuthorOverride,
    clearProjectOverride,
    saveBackToAuthor,
    saveBackToProject,
    openEditPane,
    closeEditPane,
    saveContent,
    deleteContent,
    createVersion,
    updateVersion,
    rateContent,
    rateVersion,
    setActiveVersion,
    loadContent,
    newContent,
    loadProjectContents,
    uploadSourceFile,
    deleteSourceFile,
    generate,
    regenerateBlocks,
    translate,
    stopGeneration,
    loadPublications,
    createPublication,
    deletePublication,
    generatePlan,
    stopPlanGeneration,
    resetPlanGeneration,
    removePlanEntry,
    updatePlanEntryTitle,
    updatePlanEntryDescription,
    savePlan,
    selectPublication,
    deselectPublication,
    selectEntry,
    updatePublicationName,
    updatePublicationEntry,
    addPublicationEntry,
    removePublicationEntry,
    appendEntriesToPublication,
    loadCmsConnections,
    createCmsConnection,
    deleteCmsConnection,
    testCmsConnection,
    setCmsConnection,
    generateCmsMetadata,
    saveCmsMetadata,
    pushToCms,
  }

  return store
}
